import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CaptainLocation, RealtimeMessage } from '@ridemaadi/types';
import { locationApi, locationKey } from './location';
import { rideKeys } from './hooks';

/**
 * Subscribes to the ride's realtime channel. Live messages update the query
 * cache immediately; polling (configured on the individual queries) remains the
 * resilient fallback when the socket is unavailable.
 */
export function useRideRealtime(rideId: string | undefined, enabled: boolean) {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!rideId || !enabled) return;
    let closed = false;
    let retry: ReturnType<typeof setTimeout>;
    let ping: ReturnType<typeof setInterval>;

    const connect = async () => {
      try {
        const { url } = await locationApi.realtimeUrl(rideId);
        if (closed) return;
        const ws = new WebSocket(url);
        socketRef.current = ws;

        ws.onopen = () => {
          setConnected(true);
          ping = setInterval(() => ws.readyState === ws.OPEN && ws.send(JSON.stringify({ type: 'ping' })), 30000);
        };
        ws.onmessage = (event) => {
          const msg = JSON.parse(event.data as string) as RealtimeMessage;
          handleMessage(msg);
        };
        ws.onclose = () => {
          setConnected(false);
          clearInterval(ping);
          if (!closed) retry = setTimeout(connect, 3000);
        };
        ws.onerror = () => ws.close();
      } catch {
        if (!closed) retry = setTimeout(connect, 5000);
      }
    };

    const handleMessage = (msg: RealtimeMessage) => {
      switch (msg.type) {
        case 'captain_location': {
          const next: CaptainLocation = {
            latitude: msg.latitude,
            longitude: msg.longitude,
            accuracyM: msg.accuracyM,
            updatedAt: msg.updatedAt,
          };
          qc.setQueryData(locationKey(rideId), next);
          break;
        }
        case 'broadcast':
          qc.invalidateQueries({ queryKey: ['rides', 'broadcasts', rideId] });
          break;
        case 'checkin':
          qc.invalidateQueries({ queryKey: ['rides', 'checkin-progress', rideId] });
          qc.invalidateQueries({ queryKey: ['rides', 'my-checkins', rideId] });
          break;
        case 'status_change':
          qc.invalidateQueries({ queryKey: rideKeys.detail(rideId), refetchType: 'active' });
          qc.invalidateQueries({ queryKey: rideKeys.mine, refetchType: 'active' });
          break;
        case 'sos_alert':
        case 'sos_cleared':
        case 'sos_response':
          qc.invalidateQueries({ queryKey: ['rides', 'sos', 'mine', rideId] });
          break;
        case 'ride_started':
          qc.invalidateQueries({ queryKey: rideKeys.detail(rideId), refetchType: 'active' });
          qc.invalidateQueries({ queryKey: rideKeys.mine, refetchType: 'active' });
          break;
        default:
          break;
      }
    };

    void connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      clearInterval(ping);
      socketRef.current?.close();
    };
  }, [rideId, enabled, qc]);

  return { connected };
}
